import clsx from 'clsx';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import {
    Alert,
    Button,
    Disclosure,
    DisclosureChevron,
    DisclosurePanel,
    DisclosureTrigger,
    ListSkeleton,
    renderIcon,
    Tooltip,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import {
    AGENT_TASKS_DEF,
    type GitBookAgentClient,
    type GitBookAgentClientState,
    GitBookAgentClientStatus,
    useAgentState,
} from '../Agent';
import { BasicDocument, encodeDocumentForAPI, useBasicEditorPersistedValue } from '../BasicEditor';
import { GitBookAgentAvatar } from '../Users';
import { AgentChatInput } from './AgentChatInput';
import { ToolCallsSummary } from './ToolCallsSummary';

export interface AgentChatBlankslateProps {
    /** Message a message in the chat */
    postMessage: (message: string) => void;
}

/**
 * Chat view for the agent.
 */
export function AgentChat(props: {
    agent: GitBookAgentClient;
    currentContext?: api.AIMessageContext;

    /**
     * Class name to apply to the chat container.
     */
    className?: string;

    /**
     * Component to display when the chat is empty.
     *
     * @param props - Props for the component.
     * @returns The component to display.
     */
    blankslate?: React.ComponentType<AgentChatBlankslateProps>;

    /**
     * If false, the message input will not be displayed.
     * @default true
     */
    withInput?: boolean;

    /**
     * Placeholder for the message input.
     */
    placeholder?: string;

    /**
     * Callback to track when a message is posted.
     *
     * @param input - The input to track.
     */
    onTrackPostMessage?: (input: {
        messageSource: 'suggestion' | 'input';
        message: string;
        previousMessagesCount: number;
    }) => void;
}) {
    const {
        agent,
        currentContext,
        className,
        placeholder,
        blankslate,
        onTrackPostMessage,
        withInput = true,
    } = props;
    const state = useAgentState(agent);
    const { messages, status, suggestions } = state;

    const {
        value: message,
        setValue: setMessage,
        resetValue,
    } = useBasicEditorPersistedValue('agentMessage');

    const { scrollRef, onScroll } = useScrollChat(state);

    const onPostSuggestion = (suggestion: string) => {
        agent.postMessage({
            role: api.AIMessageRole.User,
            content: suggestion,
            context: currentContext,
        });

        onTrackPostMessage?.({
            messageSource: 'suggestion',
            message: suggestion,
            previousMessagesCount: agent.state.messages.length,
        });
    };

    let foundAgentMessage = false;
    return (
        <div className={clsx('flex flex-1 flex-col overflow-hidden', className)}>
            <div
                ref={scrollRef}
                onScroll={onScroll}
                className="flex flex-1 flex-col gap-3 overflow-auto p-4"
            >
                {messages.length === 0 ? (
                    status === GitBookAgentClientStatus.IDLE ? (
                        blankslate ? (
                            React.createElement(blankslate, {
                                postMessage: onPostSuggestion,
                            })
                        ) : null
                    ) : (
                        <ListSkeleton className="p-4" />
                    )
                ) : (
                    messages.map((message, index) => {
                        const isFirstAgentMessage =
                            message.role === api.AIMessageRole.Assistant && !foundAgentMessage;
                        if (isFirstAgentMessage) {
                            foundAgentMessage = true;
                        }

                        return (
                            <ChatMessage
                                key={message.id}
                                message={message}
                                loading={
                                    status === GitBookAgentClientStatus.PROCESSING &&
                                    message.role === api.AIMessageRole.Assistant &&
                                    index === messages.length - 1
                                }
                                isFirstAgentMessage={isFirstAgentMessage}
                            />
                        );
                    })
                )}

                {suggestions.length > 0 ? (
                    <div className="flex flex-row flex-wrap justify-end gap-2">
                        {suggestions.map((suggestion) => (
                            <Button
                                kind="pill"
                                key={suggestion}
                                label={suggestion}
                                size="small"
                                onPress={() => {
                                    onPostSuggestion(suggestion);
                                }}
                            />
                        ))}
                    </div>
                ) : null}
            </div>
            {status === GitBookAgentClientStatus.ERRORED ? (
                <div className="flex">
                    <Alert
                        kind="danger"
                        size="xsmall"
                        details="An error occurred while processing your request. Reset the conversation to try again."
                        className="m-3"
                    />
                </div>
            ) : withInput ? (
                <AgentChatInput
                    value={message}
                    setValue={setMessage}
                    onSubmit={() => {
                        agent.postMessage({
                            role: api.AIMessageRole.User,
                            content: encodeDocumentForAPI(message.document),
                            context: currentContext,
                        });
                        resetValue();

                        onTrackPostMessage?.({
                            messageSource: 'input',
                            message: message.document.text,
                            previousMessagesCount: agent.state.messages.length,
                        });
                    }}
                    submittable={status === GitBookAgentClientStatus.IDLE}
                    placeholder={placeholder}
                    className={clsx(
                        'm-3 rounded-lg border border-base',
                        message.isFocused && 'border-primary shadow-elevated'
                    )}
                />
            ) : null}
        </div>
    );
}

function ChatMessage(props: {
    message: api.AIMessage;
    loading: boolean;
    isFirstAgentMessage: boolean;
}) {
    const { message, loading, isFirstAgentMessage } = props;
    const isEmpty = isMessageEmpty(message);

    const steps = React.useMemo(() => mergeMessageSteps(message.steps), [message.steps]);

    return (
        <>
            {message.response && !message.response?.previous && isFirstAgentMessage ? (
                <ExpiredConversationWarning />
            ) : null}
            <div className={clsx('flex gap-2')}>
                {message.role === api.AIMessageRole.Assistant ? (
                    <div className="sticky top-0.5 z-10 mt-0.5 self-start">
                        <GitBookAgentAvatar size="md" loading={loading} />
                    </div>
                ) : null}
                <div className="flex flex-1 flex-col gap-2">
                    {message.task ? (
                        <div className="sticky top-0 right-0 left-0 z-20 flex flex-row">
                            <div className="my-1 flex flex-row items-center gap-2 rounded-full bg-muted px-3 py-1.5">
                                {renderIcon(AGENT_TASKS_DEF[message.task.type].icon, {
                                    className: 'size-4 text-muted',
                                })}
                                <div
                                    className={clsx(
                                        'text-muted text-ui-small',
                                        loading ? 'animate-pulse' : ''
                                    )}
                                >
                                    {AGENT_TASKS_DEF[message.task.type].label}
                                </div>
                            </div>
                        </div>
                    ) : null}
                    <div
                        className={clsx(
                            'flex flex-1',
                            message.role === api.AIMessageRole.User
                                ? 'ml-9 flex-row-reverse'
                                : 'mr-9'
                        )}
                    >
                        <div
                            className={clsx(
                                'flex select-text flex-col rounded text-ui-base',
                                message.role === api.AIMessageRole.User
                                    ? 'bg-standout-base text-base'
                                    : 'text-base'
                            )}
                        >
                            {loading && isEmpty ? (
                                <div className="flex animate-pulse flex-col p-2 py-1.5 text-muted text-ui-base">
                                    Thinking…
                                </div>
                            ) : null}
                            {steps.map((step, index) => {
                                return (
                                    <div key={index} className="flex flex-col">
                                        {step.reasoning && step.reasoning.nodes.length > 0 ? (
                                            <ChatMessageReasoning reasoning={step.reasoning} />
                                        ) : null}
                                        {step.content.nodes.length > 0 ? (
                                            <BasicDocument
                                                document={step.content}
                                                className="flex-auto overflow-auto p-2 py-1.5"
                                                typographyClassName={
                                                    message.role === api.AIMessageRole.User
                                                        ? 'text-ui-small'
                                                        : 'text-ui-base'
                                                }
                                            />
                                        ) : null}
                                        {step.toolCalls && step.toolCalls.length > 0 ? (
                                            <ToolCallsSummary
                                                toolCalls={step.toolCalls}
                                                loading={loading}
                                            />
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function ChatMessageReasoning(props: { reasoning: api.JSONDocument }) {
    const { reasoning } = props;

    return (
        <Disclosure>
            <div className="mt-1 flex flex-row">
                <DisclosureTrigger>
                    <Button
                        kind="blank"
                        size="small"
                        label="Thought"
                        trailingIcon={<DisclosureChevron />}
                    />
                </DisclosureTrigger>
            </div>
            <DisclosurePanel>
                <BasicDocument
                    document={reasoning}
                    className="ml-3 flex-auto overflow-auto border-l border-l-base p-2 pl-3"
                    typographyClassName="text-ui-small text-muted"
                />
            </DisclosurePanel>
        </Disclosure>
    );
}

function ExpiredConversationWarning() {
    return (
        <div className="text-muted text-ui-small">
            The agent conversation memory has{' '}
            <Tooltip label="After 30 days of inactivity, the agent conversation expires and the agent will lose context on previous messages.">
                <span className="cursor-help underline decoration-dotted">expired</span>
            </Tooltip>
            .
        </div>
    );
}

function isNearBottom(el: HTMLDivElement) {
    const threshold = 24; // px tolerance for "near bottom"
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distance <= threshold;
}

/**
 * Hook to manage auto-scrolling behavior in the chat.
 */
function useScrollChat(state: GitBookAgentClientState) {
    const disableAutoScroll = React.useRef(false);
    const scrollRef = React.useRef<HTMLDivElement | null>(null);

    // When messages are updated, we scroll to the bottom.
    React.useEffect(() => {
        if (scrollRef.current && state.messages.length > 0 && !disableAutoScroll.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [state]);

    // Reset things when the AI finished writing.
    React.useEffect(() => {
        if (state.status === GitBookAgentClientStatus.IDLE) {
            disableAutoScroll.current = false;
        }
    }, [state.status]);

    // We only disable auto-scroll if they move away from the bottom while the AI is writing.
    const onScroll = useEventCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        disableAutoScroll.current =
            state.status === GitBookAgentClientStatus.PROCESSING && !isNearBottom(el);
    });

    return {
        scrollRef,
        onScroll,
    };
}

/**
 * Merge consecutive message steps that are simple tool calls into a single step.
 * We do this so that we can display similar tool calls together.
 */
function mergeMessageSteps(steps: api.AIMessageStep[]) {
    const result: api.AIMessageStep[] = [];

    for (const step of steps) {
        if (isMessageStepContentEmpty(step)) {
            if (result[result.length - 1]) {
                result[result.length - 1] = {
                    ...result[result.length - 1],
                    toolCalls: (result[result.length - 1].toolCalls ?? []).concat(
                        step.toolCalls ?? []
                    ),
                };
            } else {
                result.push(step);
            }
        } else {
            result.push(step);
        }
    }

    return result;
}

function isMessageEmpty(message: api.AIMessage) {
    return message.steps.length === 0 || isMessageStepEmpty(message.steps[0]);
}

function isMessageStepEmpty(step: api.AIMessageStep) {
    return isMessageStepContentEmpty(step) && (!step.toolCalls || step.toolCalls.length === 0);
}

function isMessageStepContentEmpty(step: api.AIMessageStep) {
    return (
        (!step.reasoning || step.reasoning.nodes.length === 0) && step.content.nodes.length === 0
    );
}
